import React from 'react';

import {
	Check,
	ChevronDown,
	ChevronUp,
	Clock,
	RefreshCw,
	XCircle,
} from 'lucide-react';

import ProgressIcon from '../../assets/icons/progress.svg';
import { ensureUtcTimestamp, formatDuration } from '../../utils/utils';
import Button from '../common/Button';
import ProcessingLogs from '../ProcessingLogs';
import { hasCreditSettlementFailure } from '../processingStatus';

import type { ProcessingLogs as ProcessingLogsData } from '../../api';
import type { LucideIcon } from 'lucide-react';

// The platform run reports no measured progress, so the processing view shows an
// indeterminate bar and no percentage. The page keeps polling and flips to the
// completed view when done.
const PROCESSING_TITLE = 'Analyzing your statement…';

// Every upload and rerun starts with this log, and ends with one of the others.
// platform_recalculation_completed is left out: Customize Quote writes it later.
const RUN_START_STEP = 'platform_dispatch_queued';
const RUN_END_STEPS = new Set([
	'platform_extraction_completed',
	'platform_run_failed',
]);

const logTime = (timestamp?: string): number =>
	timestamp ? new Date(ensureUtcTimestamp(timestamp)).getTime() : Number.NaN;

/**
 * Seconds the latest run took, from its dispatch log to its last end log. The
 * log keeps every earlier run and recalculation, so only the latest run counts.
 * Null when that run has no start or no end yet.
 */
export const elapsedSeconds = (
	logs: ProcessingLogsData['logs'] | undefined,
): number | null => {
	const starts = (logs ?? [])
		.filter((log) => log.step_name === RUN_START_STEP)
		.map((log) => logTime(log.created_at))
		.filter((t) => !Number.isNaN(t));
	if (starts.length === 0) return null;
	const start = Math.max(...starts);
	const ends = (logs ?? [])
		.filter((log) => RUN_END_STEPS.has(log.step_name))
		.map((log) => logTime(log.completed_at ?? log.created_at))
		.filter((t) => !Number.isNaN(t) && t >= start);
	if (ends.length === 0) return null;
	const seconds = Math.round((Math.max(...ends) - start) / 1000);
	return seconds > 0 ? seconds : null;
};

// The worker keeps the latest platform phase and % on this one log row (ROC-80).
export const RUN_PROGRESS_STEP = 'platform_progress';
// A new run starts on upload/rerun (dispatch) or on an automatic retry.
const PROGRESS_RESET_STEPS = new Set([RUN_START_STEP, 'platform_run_retry']);

// Customer wording for the platform's run phases; anything else shows the generic line.
const PHASE_LABELS: Record<string, string> = {
	entity_validation: 'Getting ready',
	file_processing: 'Reading your statement',
	file_validation: 'Reading your statement',
	validating: 'Checking the document',
	policy_evaluation: 'Checking the document',
	agent_execution: 'Analyzing your fees',
	narrative_generation: 'Writing the summary',
	completing: 'Finishing up',
};

export interface RunProgress {
	percent: number;
	phase: string | null;
}

/** Measured progress of the latest run, or null when it has not reported any yet. */
export const runProgress = (
	logs: ProcessingLogsData['logs'] | undefined,
): RunProgress | null => {
	const row = (logs ?? []).find((log) => log.step_name === RUN_PROGRESS_STEP);
	const percent = Number(row?.step_data?.progress_percent);
	if (!row || !Number.isFinite(percent)) return null;
	const latestStart = Math.max(
		...(logs ?? [])
			.filter((log) => PROGRESS_RESET_STEPS.has(log.step_name))
			.map((log) => logTime(log.created_at)),
	);
	// An earlier run's progress must not show on a rerun or an automatic retry.
	if (logTime(row.completed_at ?? row.created_at) < latestStart) return null;
	const phase = row.step_data?.phase;
	return {
		percent: Math.max(0, Math.min(100, percent)),
		phase: typeof phase === 'string' && phase ? phase : null,
	};
};

/** The customer-facing name of a run phase, or null for one we have no wording for. */
export const phaseLabel = (phase: string | null): string | null =>
	(phase && PHASE_LABELS[phase]) || null;

// Customer-facing copy only. The raw failure reason (which can carry upstream
// error text) stays in the processing log below, never in this banner.
export const FAILED_TITLE = 'Statement processing failed';
export const FAILED_MESSAGE =
	"We couldn't finish analyzing this statement. Check the processing log below, then run the analysis again.";
export const COMPLETED_TITLE = 'Statement Processing Completed';

interface Presentation {
	Icon: LucideIcon;
	title: string;
	message?: string;
	header: string;
	chip: string;
	iconColor: string;
	titleColor: string;
}

const NEUTRAL: Omit<Presentation, 'Icon' | 'title'> = {
	header: 'bg-white border-neutral-200',
	chip: 'bg-neutral-100',
	iconColor: 'text-neutral-500',
	titleColor: 'text-neutral-900',
};

// Status → presentation, as data. Adding a backend status is a row here, not
// a fifth branch; anything not listed renders as UNKNOWN. `processing` is a
// different layout (animated stages), not a variant, and keeps its own render.
const PRESENTATION: Record<string, Presentation> = {
	failed: {
		Icon: XCircle,
		title: FAILED_TITLE,
		message: FAILED_MESSAGE,
		header: 'bg-error-50 border-error-200',
		chip: 'bg-error-100',
		iconColor: 'text-error-600',
		titleColor: 'text-error-800',
	},
	completed: {
		Icon: Check,
		title: COMPLETED_TITLE,
		header: 'bg-white border-neutral-200',
		chip: 'bg-violet-100',
		iconColor: 'text-indigo-700',
		titleColor: 'text-neutral-900',
	},
	uploaded: {
		...NEUTRAL,
		Icon: Clock,
		title: 'Statement uploaded — processing has not started',
	},
	cancelled: {
		...NEUTRAL,
		Icon: Clock,
		title: 'Statement processing was cancelled',
	},
};
const UNKNOWN: Presentation = {
	...NEUTRAL,
	Icon: Clock,
	title: 'Statement processing status is unavailable',
};

interface ProcessingStepProps {
	isCollapsed: boolean;
	onToggle: () => void;
	/** Backend `current_status`; anything outside the known set renders neutrally. */
	status: string;
	logsData?: ProcessingLogsData;
	onRetry: () => void;
	/** Opens the rerun flow. Rendered as the recovery action when processing failed. */
	onRerun?: () => void;
	isRerunning?: boolean;
}

const ProcessingStep: React.FC<ProcessingStepProps> = ({
	isCollapsed,
	onToggle,
	status,
	logsData,
	onRetry,
	onRerun,
	isRerunning = false,
}) => {
	const elapsed = elapsedSeconds(logsData?.logs);
	const totalDuration = elapsed === null ? null : formatDuration(elapsed);

	const toggleButton = (
		<button
			className="p-1 hover:bg-neutral-100 rounded-sm"
			aria-label={isCollapsed ? 'Show processing log' : 'Hide processing log'}
		>
			{isCollapsed ? (
				<ChevronDown className="w-4 h-4 text-neutral-500" />
			) : (
				<ChevronUp className="w-4 h-4 text-neutral-500" />
			)}
		</button>
	);

	const logs = !isCollapsed && (
		<div>
			<ProcessingLogs logsData={logsData} onRetry={onRetry} />
		</div>
	);

	if (status === 'processing') {
		const progress = runProgress(logsData?.logs);
		return (
			<div className="transition-all duration-500">
				<div className="w-full bg-white border-b border-gray-200">
					<div className="w-full px-6 py-5">
						<div className="flex items-center gap-3">
							<div className="w-9 h-9 bg-violet-100 rounded-full flex items-center justify-center animate-pulse">
								<ProgressIcon className="w-5 h-5 text-indigo-700" />
							</div>
							<div className="flex flex-col">
								<span className="text-base font-medium text-neutral-900 font-primary">
									{PROCESSING_TITLE}
								</span>
								<span className="text-sm text-neutral-500 font-primary">
									{(progress && phaseLabel(progress.phase)) ??
										'We\u2019re analyzing your statement. You can keep working \u2014 this page updates automatically when it\u2019s ready.'}
								</span>
							</div>
						</div>
					</div>

					{progress ? (
						<div
							className="w-full h-1 bg-gray-200"
							role="progressbar"
							aria-label="Analyzing your statement"
							aria-valuenow={Math.round(progress.percent)}
							aria-valuemin={0}
							aria-valuemax={100}
						>
							{/* Phases done, not time: pulse while the current phase runs. */}
							<div
								className="h-full bg-purple-600 animate-pulse transition-all duration-700 ease-out"
								style={{ width: `${progress.percent}%` }}
							/>
						</div>
					) : (
						// Indeterminate until the run reports its first phase.
						<div
							className="w-full h-1 bg-purple-200 animate-pulse"
							role="progressbar"
							aria-label="Analyzing your statement"
						/>
					)}
				</div>
			</div>
		);
	}

	const view = PRESENTATION[status] ?? UNKNOWN;
	// Credit-settlement failures keep their own legacy action inside the log;
	// showing the rerun button too would offer two competing recoveries.
	const showRerun =
		status === 'failed' &&
		Boolean(onRerun) &&
		!hasCreditSettlementFailure(logsData);
	const showDuration = status === 'completed' && totalDuration !== null;

	// Collapsing hides the log (it is simply not rendered); the banner itself
	// is never clipped, so a two-line failed banner stays readable.
	return (
		<div className="transition-all duration-500">
			<div
				className={`w-full px-6 py-4 border-b cursor-pointer ${view.header}`}
				onClick={onToggle}
			>
				<div className="w-full flex justify-between items-center gap-4">
					<div className="flex items-center gap-3 min-w-0">
						<div
							className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${view.chip}`}
						>
							<view.Icon className={`w-5 h-5 ${view.iconColor}`} />
						</div>
						<div className="flex flex-col min-w-0">
							<div className="flex items-center gap-2">
								<span
									className={`text-base font-medium font-primary ${view.titleColor}`}
								>
									{view.title}
								</span>
								{toggleButton}
							</div>
							{view.message && (
								<span className="text-sm text-error-700 font-primary">
									{view.message}
								</span>
							)}
						</div>
					</div>
					{showRerun && (
						<Button
							variant="outline"
							size="sm"
							icon={RefreshCw}
							loading={isRerunning}
							onClick={(event) => {
								event.stopPropagation();
								onRerun?.();
							}}
						>
							Retry analysis
						</Button>
					)}
					{showDuration && (
						<div className="flex items-center gap-2 text-sm text-success-500 font-primary">
							<Clock className="w-4 h-4" />
							<span className="font-medium">
								Statement processed in {totalDuration}
							</span>
						</div>
					)}
				</div>
			</div>
			{logsData && logs}
		</div>
	);
};

export default ProcessingStep;
